import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import type { StoreAiCopyDraft } from './api';

/**
 * Store AI copy rewrite rules and drafts (flag `store_ai_copy`, MOT-153 /
 * estimate row B20). Mirrors `src/types/storeAiCopy.ts`.
 */
export type StoreAiCopyLength = 'short' | 'medium' | 'long';

export interface StoreAiCopyBrandNote {
  brand: string;
  note: string;
}

export interface StoreAiCopyRules {
  voice?: string;
  titlePattern?: string;
  bannedPhrases?: string[];
  descriptionLength?: StoreAiCopyLength;
  brandNotes?: StoreAiCopyBrandNote[];
}

export interface StoreAiCopyUsage {
  month: string;
  used: number;
  /** -1 is no cap; 0 is none. */
  limit: number;
  remaining: number;
  resetsAt: string;
}

export interface StoreAiCopyRulesResponse {
  rules: StoreAiCopyRules;
  usage: StoreAiCopyUsage;
  planIncluded: boolean;
}

export interface StoreAiCopyGenerateResult {
  draft: StoreAiCopyDraft;
  usage: StoreAiCopyUsage;
  cached: boolean;
}

export const STORE_AI_COPY_RULES_KEY = ['store-ai-copy-rules'] as const;
export const AI_COPY_BUDGET_EXHAUSTED_CODE = 'ai_copy_budget_exhausted';

/** Thrown for a 429 so the panel can say how many were used and when the month resets. */
export class AiCopyBudgetError extends Error {
  code = AI_COPY_BUDGET_EXHAUSTED_CODE;
  constructor(message: string, public details: StoreAiCopyUsage | null) {
    super(message);
  }
}

async function readJson(response: Response, fallback: string): Promise<any> {
  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body?.success) {
    const message = body?.error?.message || body?.message || fallback;
    if (response.status === 429 && body?.error?.code === AI_COPY_BUDGET_EXHAUSTED_CODE) {
      throw new AiCopyBudgetError(message, body.error.details ?? null);
    }
    throw new Error(message);
  }
  return body;
}

const jsonHeaders = async () => ({ ...(await getAuthHeaders()), 'Content-Type': 'application/json' });

export async function fetchStoreAiCopyRules(fallback: string): Promise<StoreAiCopyRulesResponse> {
  const body = await readJson(await makeApiCall('/store/ai-copy/rules', { headers: await getAuthHeaders() }), fallback);
  return body.data as StoreAiCopyRulesResponse;
}

export async function saveStoreAiCopyRules(rules: StoreAiCopyRules, fallback: string): Promise<StoreAiCopyRules> {
  const body = await readJson(
    await makeApiCall('/store/ai-copy/rules', { method: 'PUT', headers: await jsonHeaders(), body: JSON.stringify(rules) }),
    fallback,
  );
  return body.data.rules as StoreAiCopyRules;
}

export async function generateStoreAiCopy(productId: string, fallback: string): Promise<StoreAiCopyGenerateResult> {
  const body = await readJson(
    await makeApiCall(`/store/ai-copy/products/${encodeURIComponent(productId)}/generate`, { method: 'POST', headers: await jsonHeaders() }),
    fallback,
  );
  return body.data as StoreAiCopyGenerateResult;
}

export async function acceptStoreAiCopy(
  productId: string,
  fields: Array<'title' | 'description'> | undefined,
  fallback: string,
): Promise<{ overrideTitle: string | null; overrideDescription: string | null }> {
  const body = await readJson(
    await makeApiCall(`/store/ai-copy/products/${encodeURIComponent(productId)}/accept`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify(fields ? { fields } : {}),
    }),
    fallback,
  );
  return body.data;
}

export async function discardStoreAiCopy(productId: string, fallback: string): Promise<void> {
  await readJson(
    await makeApiCall(`/store/ai-copy/products/${encodeURIComponent(productId)}/discard`, { method: 'POST', headers: await jsonHeaders() }),
    fallback,
  );
}
